/**
 * Penyunting susunan lembar log.
 *
 * Empat hal disetel di sini, dan keempatnya sebelumnya adalah konstanta di
 * dalam kode layar Workstation: berapa track yang aktif dan apa isinya, sumbu
 * mendatar tiap track beserta kedua ujung skalanya, serta warna, jenis dan
 * ketebalan setiap kurva.
 *
 * Ia berdiri sebagai berkasnya sendiri karena panjangnya sepadan dengan layar
 * yang memuatnya, dan karena sebuah penyunting yang bisa dirender sendirian
 * bisa diuji sendirian juga — tanpa lebih dulu memalsukan seluruh API,
 * autentikasi dan konteks toast di belakang layar itu.
 */
import { EditColoursButton } from "./PaletteEditor";
import { useState, type ReactNode } from "react";

import { ColorPicker } from "./ColorPicker";
import { defaultRamp, rampGradient, rampKeys, rampLabel } from "../lib/colorscale";
import { Icon } from "./icons";
import type { TrackCurve, TrackSpec } from "./LogTracks";
import { Button, Caret, Dropdown, NumberField, Notice, Panel, Segmented, Slider, Toggle } from "./ui";
import {
  CURVE_STYLES,
  formatScaleValue,
  addCurve,
  addTrack,
  defaultCurveColor,
  makeDescribedTrack,
  makeLrlcTrack,
  makeTrack,
  moveTrack,
  removeCurve,
  removeTrack,
  suggestedScale,
  updateCurve,
  updateTrack,
  type CrossFill,
  type CurveStyle,
  type FillAnchor,
  type TrackConfig,
} from "../lib/tracklayout";
import type { CurveData } from "../types";

/** Warna bawaan arsiran silang — hijau yang sama dengan track PHIE/SW bawaan. */
const CROSS_FILL_COLOR = "var(--green)";

/** Ketiga pilihan arsiran satu kurva, dinamai menurut ujung skalanya sendiri.
 *
 *  "Kiri" dan "kanan" berbohong pada sumbu yang dibalik — NPHI dan SW memang
 *  lazim dibaca terbalik — dan sebuah tombol yang mengarsir sisi berlawanan
 *  dengan namanya lebih buruk daripada tidak ada tombolnya sama sekali. */
/** Ke mana gradasi arsiran mengikuti nilai: melintang, atau menurun. */
const FILL_GRADIENT_OPTIONS = [
  { value: "value", label: "Value per depth" },
  { value: "across", label: "Width" },
];

const FILL_OPTIONS = [
  { value: "none" as const, label: "None" },
  { value: "min" as const, label: "To min" },
  { value: "max" as const, label: "To max" },
];

/**
 * Tombol kecil untuk baris yang padat.
 *
 * `IconButton` berukuran 28 piksel dan membawa latarnya sendiri; tiga di
 * antaranya di kepala panel selebar 262 piksel tidak menyisakan ruang untuk
 * judul track yang justru menjadi isi baris itu.
 */
export function MiniButton({
  children,
  onClick,
  title,
  disabled,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        flex: "none",
        padding: 0,
        borderRadius: 4,
        border: "1px solid var(--border-control)",
        background: "transparent",
        color: disabled ? "var(--ink-7)" : danger ? "var(--rose)" : "var(--ink-4)",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
        font: "500 11px var(--font-mono)",
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)", letterSpacing: ".05em" }}>
        {label}
      </span>
      {children}
    </div>
  );
}

function BareInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      style={{
        height: 24,
        minWidth: 0,
        width: "100%",
        padding: "0 7px",
        background: "var(--panel-inset)",
        border: "1px solid var(--line-strong)",
        borderRadius: 5,
        font: "400 11px var(--font-sans)",
        color: "var(--ink)",
        outline: "none",
      }}
    />
  );
}

/**
 * Susunan lembar log sebagai sesuatu yang bisa disunting.
 *
 * Empat hal disetel di sini, dan keempatnya sebelumnya adalah konstanta di
 * dalam kode: berapa track yang aktif dan apa isinya, sumbu mendatar tiap
 * track beserta kedua ujung skalanya, serta warna, jenis dan ketebalan setiap
 * kurva. Angka skala yang ditampilkan adalah angka yang benar-benar dipakai
 * menggambar — termasuk ketika ia berasal dari skala tampilan bawaan kurva —
 * sehingga kotak yang kosong tidak berarti "tidak diketahui" melainkan
 * "ikut bawaan", dan keduanya tetap bisa dibaca sebagai bilangan.
 */
export function TrackEditor({
  tracks,
  onChange,
  resolved,
  available,
  selected,
  onSelect,
  shadingDefault = CROSS_FILL_COLOR,
  layout,
}: {
  tracks: TrackConfig[];
  onChange: (next: TrackConfig[]) => void;
  /** Track sebagaimana benar-benar tergambar, sumber angka skala efektif. */
  resolved: TrackSpec[];
  available: CurveData[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  /**
   * Warna arsiran yang dipakai track yang belum punya warnanya sendiri —
   * bawaan proyek, dari **View → Log sheet colours**.
   *
   * Track yang sudah membawa warnanya tetap memakainya, dan itu disengaja:
   * susunan track adalah milik pembacanya, sedangkan yang di sini adalah titik
   * berangkatnya. Sebuah bawaan proyek yang menimpa warna yang sudah dipilih
   * orang akan mengubah lembar yang sedang dibaca tanpa ia menyentuh apa pun.
   */
  shadingDefault?: string;
  /**
   * Yang boleh dikerjakan terhadap **susunannya** — pindah, buang, tambah.
   *
   * Tidak ada berarti susunannya bukan milik yang membacanya: yang tersisa
   * hanya cara menggambar — skala, warna, gaya, ketebalan, arsiran. Dipakai
   * oleh lembar mana pun yang kolomnya ditentukan kode dan bukan penggunanya.
   *
   * Ada berarti susunannya miliknya, dan dua hal harus disebut oleh yang
   * memuatnya sebab keduanya berbeda di tiap lembar: ke mana "Restore the
   * default layout" mengembalikannya, dan apakah kedua kolom istimewa —
   * litologi terdeskripsi dan kelas LRLC — masuk akal di sana. Lembar LRLC
   * tidak menawarkan keduanya: kolom kelasnya sudah ada di susunan bawaannya
   * dan digambar dari hitungan yang sedang berjalan, bukan dari kurva
   * `LRLC_CLASS` yang sudah diterbitkan.
   */
  layout?: {
    onRestore: () => void;
    extras?: boolean;
    /**
     * Kolom bawaan yang sedang tidak ada di susunan, dan bisa dipanggil pulang
     * satu per satu.
     *
     * Disebut oleh yang memuatnya sebab hanya ia yang tahu susunan bawaannya.
     * Lembar yang kolomnya dibangun dari kurva tidak perlu menyebutnya: kolom
     * apa pun yang dibuang di sana bisa dibangun kembali dari "New track from
     * curve…". Lembar LRLC tidak begitu — empat kolomnya adalah pita tanpa
     * kurva, dan sisanya membawa arsiran, sumbu dan garis ambang yang tidak
     * lahir dari nama sebuah kurva.
     */
    restorable?: { id: string; title: string }[];
    /** Memanggil pulang salah satu dari `restorable`. */
    onRestoreOne?: (id: string) => void;
  };
}) {
  const locked = layout === undefined;
  const [openCurve, setOpenCurve] = useState<string | null>(null);

  const drawn = (trackId: string, name: string): TrackCurve | null =>
    resolved.find((track) => track.id === trackId)?.curves.find((curve) => curve.name === name) ?? null;

  return (
    <>
      {tracks.length === 0 && (
        <Notice>
          {locked
            ? "Nothing to set up: this sheet drew no track."
            : "This sheet has no track. Add one below."}
        </Notice>
      )}

      {tracks.map((track, index) => {
        // Hanya track yang sedang dipilih yang terbuka. Lima panel yang semuanya
        // terbentang menjadikan tab ini gulungan sepanjang tiga layar, tempat
        // menyetel satu kurva berarti lebih dulu menggulir melewati empat track
        // yang tidak sedang ditanyakan. Mengeklik kepala track di lembar log
        // membuka panel yang bersangkutan di sini, jadi keduanya satu gerakan.
        const open = selected === track.id;
        const spare = available.filter((curve) => !track.curves.some((c) => c.name === curve.mnemonic));
        return (
          <Panel
            key={track.id}
            title={
              <button
                type="button"
                onClick={() => onSelect(open ? null : track.id)}
                title={open ? "Close track settings" : `Set up track ${track.title}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  font: "inherit",
                  color: "inherit",
                  letterSpacing: "inherit",
                  textTransform: "inherit",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    // Panah menghadap ke bawah saat panelnya terbuka dan ke
                    // kanan saat tertutup — arah yang sama dipakai pohon proyek
                    // di rel sebelah kiri.
                    transform: open ? undefined : "rotate(-90deg)",
                    transition: "transform 120ms",
                  }}
                >
                  <Caret color={open ? "var(--accent)" : "var(--ink-5)"} size={7} />
                </span>
                {track.title || "Track"}
              </button>
            }
            meta={
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: track.scale === "log" ? "var(--amber)" : "var(--ink-6)" }}>
                  {track.scale === "log" ? "LOG" : "LIN"}
                </span>
                <span>{index + 1}/{tracks.length}</span>
              </span>
            }
            style={
              open
                ? { borderColor: "var(--accent)", boxShadow: "0 0 0 1px var(--accent-zone-line)" }
                : undefined
            }
            bodyStyle={{ padding: open ? 10 : "7px 10px", gap: open ? 10 : 0 }}
            actions={
              locked ? undefined : (
              <span style={{ display: "flex", gap: 3 }}>
                <MiniButton
                  title="Move left"
                  disabled={index === 0}
                  onClick={() => onChange(moveTrack(tracks, track.id, -1))}
                >
                  ‹
                </MiniButton>
                <MiniButton
                  title="Move right"
                  disabled={index === tracks.length - 1}
                  onClick={() => onChange(moveTrack(tracks, track.id, 1))}
                >
                  ›
                </MiniButton>
                <MiniButton
                  title={`Delete track ${track.title}`}
                  danger
                  onClick={() => {
                    onChange(removeTrack(tracks, track.id));
                    if (open) onSelect(null);
                  }}
                >
                  {Icon.trash({ size: 10 })}
                </MiniButton>
              </span>
              )
            }
          >
            {!open && <CurveSummary track={track} available={available} />}

            {open && (
              <>
                {!locked && (
                  <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 7 }}>
                    <Field label="TITLE">
                      <BareInput
                        value={track.title}
                        placeholder="Track"
                        onChange={(value) => onChange(updateTrack(tracks, track.id, { title: value }))}
                      />
                    </Field>
                    <Field label="UNITS">
                      <BareInput
                        value={track.units}
                        placeholder="—"
                        onChange={(value) => onChange(updateTrack(tracks, track.id, { units: value }))}
                      />
                    </Field>
                  </div>
                )}

                <Field label="HORIZONTAL SCALE">
                  <Segmented
                    size="sm"
                    value={track.scale}
                    onChange={(value) => onChange(updateTrack(tracks, track.id, { scale: value }))}
                    options={[
                      { value: "linear" as const, label: "Linear" },
                      { value: "log" as const, label: "Logarithmic" },
                    ]}
                  />
                </Field>

                <Field label="COLOUR BAND">
                  <Segmented
                    size="sm"
                    value={track.spectrum ? "on" : "off"}
                    onChange={(value) =>
                      onChange(
                        updateTrack(tracks, track.id, {
                          // Setelan yang pernah ada dipulihkan apa adanya:
                          // mematikan pita untuk melihat garisnya sebentar
                          // tidak boleh membuang jepitan yang baru disetel.
                          spectrum:
                            value === "on"
                              ? (track.spectrum ?? {
                                  curve: track.curves[0]?.name,
                                  ramp: defaultRamp(track.curves[0]?.name ?? ""),
                                })
                              : undefined,
                        }),
                      )
                    }
                    options={[
                      { value: "off" as const, label: "Line" },
                      { value: "on" as const, label: "Colour band" },
                    ]}
                  />
                  {track.spectrum && (
                    <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>
                      The ramp and its clamps are set from the colour bar in the track header.
                    </span>
                  )}
                </Field>

                {/* Arsiran antara dua kurva track ini — volume pori
                    hidrokarbon pada PHIE/SW, pemisahan RHOB/NPHI pada track
                    densitas-neutron. Hanya muncul ketika ada dua kurva untuk
                    dibandingkan: sebuah setelan yang tidak bisa berlaku
                    mengajari penggunanya bahwa setelan di sini tidak berlaku. */}
                {track.curves.length >= 2 && (
                  <Field label="SHADING BETWEEN CURVES">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr auto",
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <Dropdown
                        value={track.shading?.curve ?? ""}
                        options={[
                          { value: "", label: "None" },
                          // Kurva pembandingnya sendiri tidak ditawarkan di
                          // sini: sebuah kurva tidak pernah berada di sisi
                          // nilai lebih besar daripada dirinya sendiri, dan
                          // pasangan seperti itu tidak pernah mengarsir apa
                          // pun — kegagalan yang terlihat persis seperti
                          // track yang tidak berubah.
                          ...track.curves
                            .filter((entry) => entry.name !== track.shading?.against)
                            .map((entry) => ({ value: entry.name, label: entry.name })),
                        ]}
                        onChange={(value) =>
                          onChange(
                            updateTrack(tracks, track.id, {
                              shading: value
                                ? crossFill(track, { curve: String(value) }, shadingDefault)
                                : undefined,
                            }),
                          )
                        }
                        width="100%"
                        compact
                      />
                      <Dropdown
                        value={track.shading?.against ?? ""}
                        disabled={!track.shading}
                        options={
                          track.shading
                            ? track.curves
                                .filter((entry) => entry.name !== track.shading!.curve)
                                .map((entry) => ({ value: entry.name, label: entry.name }))
                            : [{ value: "", label: "—" }]
                        }
                        onChange={(value) =>
                          onChange(
                            updateTrack(tracks, track.id, {
                              shading: crossFill(track, { against: String(value) }, shadingDefault),
                            }),
                          )
                        }
                        width="100%"
                        compact
                      />
                      <ColorPicker
                        compact
                        disabled={!track.shading}
                        value={track.shading?.color ?? shadingDefault}
                        label={`Colour of the shading between curves in ${track.title}`}
                        onChange={(value) =>
                          onChange(
                            updateTrack(tracks, track.id, {
                              shading: crossFill(track, { color: value }, shadingDefault),
                            }),
                          )
                        }
                      />
                    </div>
                    <span style={{ font: "400 9px/1.35 var(--font-sans)", color: "var(--ink-6)" }}>
                      {track.shading
                        ? `Shaded where ${track.shading.curve} passes ${track.shading.against}.`
                        : "Shades the space between two curves, where the first passes the second."}
                    </span>
                  </Field>
                )}

                <Slider
                  label="Track width"
                  value={track.flex}
                  min={0.4}
                  max={2.5}
                  step={0.05}
                  onChange={(value) => onChange(updateTrack(tracks, track.id, { flex: value }))}
                  format={(value) => `${value.toFixed(2)}×`}
                />

                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {track.curves.map((curve, curveIndex) => {
                    const key = `${track.id}:${curveIndex}`;
                    const editing = openCurve === key;
                    const shown = drawn(track.id, curve.name);
                    const absent = !available.some((entry) => entry.mnemonic === curve.name);
                    const colour = curve.color ?? defaultCurveColor(curve.name);
                    return (
                      <div
                        key={key}
                        style={{
                          border: `1px solid ${editing ? "var(--border-hover)" : "var(--line-soft)"}`,
                          borderRadius: 5,
                          padding: editing ? "7px 7px 8px" : "5px 7px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                          background: editing ? "var(--panel-inset)" : "transparent",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span
                            style={{
                              width: 14,
                              flex: "none",
                              height: 0,
                              borderTop: `${(curve.width ?? 2).toFixed(1)}px ${lineStyleName(curve.style)} ${colour}`,
                              opacity: curve.hidden || absent ? 0.4 : 1,
                            }}
                          />
                          <span
                            style={{
                              font: "500 10.5px var(--font-mono)",
                              color: absent ? "var(--ink-7)" : "var(--ink-2)",
                              textDecoration: curve.hidden ? "line-through" : undefined,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {curve.name}
                          </span>
                          {absent && (
                            <span
                              title={`${curve.name} is not on this well — its settings stay saved`}
                              style={{ font: "400 8.5px var(--font-mono)", color: "var(--ink-7)" }}
                            >
                              absent
                            </span>
                          )}
                          {!absent && !curve.hidden && shown && (
                            <span
                              style={{
                                font: "400 8.5px var(--font-mono)",
                                color: "var(--ink-7)",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {formatScaleValue(shown.min)}–{formatScaleValue(shown.max)}
                            </span>
                          )}
                          <span style={{ flex: 1, minWidth: 0 }} />
                          {!locked && (
                            <MiniButton
                              title={curve.hidden ? `Show ${curve.name}` : `Hide ${curve.name}`}
                              onClick={() =>
                                onChange(updateCurve(tracks, track.id, curveIndex, { hidden: !curve.hidden }))
                              }
                            >
                              {curve.hidden ? Icon.eyeOff({ size: 10 }) : Icon.eye({ size: 10 })}
                            </MiniButton>
                          )}
                          <MiniButton
                            title={editing ? "Close settings" : `Set up the display of ${curve.name}`}
                            onClick={() => setOpenCurve(editing ? null : key)}
                          >
                            {editing ? "−" : "+"}
                          </MiniButton>
                          {!locked && (
                            <MiniButton
                              title={`Take ${curve.name} out of the track`}
                              danger
                              onClick={() => onChange(removeCurve(tracks, track.id, curveIndex))}
                            >
                              {Icon.trash({ size: 10 })}
                            </MiniButton>
                          )}
                        </div>

                        {editing && (
                          <>
                            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 7 }}>
                              <Field label="COLOUR">
                                <ColorPicker
                                  compact
                                  value={colour}
                                  label={`Colour of ${curve.name}`}
                                  onChange={(value) =>
                                    onChange(updateCurve(tracks, track.id, curveIndex, { color: value }))
                                  }
                                />
                              </Field>
                              <Field label="CURVE STYLE">
                                <Dropdown
                                  value={curve.style ?? "solid"}
                                  options={CURVE_STYLES}
                                  onChange={(value) =>
                                    onChange(
                                      updateCurve(tracks, track.id, curveIndex, { style: value as CurveStyle }),
                                    )
                                  }
                                  width="100%"
                                  compact
                                />
                              </Field>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                              <NumberField
                                label="Left scale"
                                value={curve.min ?? shown?.min ?? 0}
                                step={0.1}
                                onChange={(value) =>
                                  onChange(updateCurve(tracks, track.id, curveIndex, { min: value }))
                                }
                              />
                              <NumberField
                                label="Right scale"
                                value={curve.max ?? shown?.max ?? 1}
                                step={0.1}
                                onChange={(value) =>
                                  onChange(updateCurve(tracks, track.id, curveIndex, { max: value }))
                                }
                              />
                              <NumberField
                                label="Width px"
                                value={curve.width ?? 1.3}
                                step={0.1}
                                min={0.4}
                                max={6}
                                onChange={(value) =>
                                  onChange(updateCurve(tracks, track.id, curveIndex, { width: value }))
                                }
                              />
                            </div>
                            <Field label="ARSIRAN">
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr auto",
                                  gap: 7,
                                  alignItems: "center",
                                }}
                              >
                                <Segmented
                                  size="sm"
                                  value={curve.fill?.from ?? "none"}
                                  onChange={(value) =>
                                    onChange(
                                      updateCurve(tracks, track.id, curveIndex, {
                                        // Warna yang pernah dipilih dipulihkan
                                        // apa adanya: mematikan arsiran sejenak
                                        // untuk melihat kurva di bawahnya tidak
                                        // boleh membuang warnanya.
                                        fill:
                                          value === "none"
                                            ? undefined
                                            : { from: value as FillAnchor, color: curve.fill?.color },
                                      }),
                                    )
                                  }
                                  options={FILL_OPTIONS}
                                />
                                <ColorPicker
                                  compact
                                  disabled={!curve.fill}
                                  value={curve.fill?.color ?? colour}
                                  label={`Shading colour of ${curve.name}`}
                                  onChange={(value) =>
                                    onChange(
                                      updateCurve(tracks, track.id, curveIndex, {
                                        fill: { from: curve.fill?.from ?? "max", color: value },
                                      }),
                                    )
                                  }
                                />
                              </div>
                              {curve.fill && (
                                <>
                                  {/* Pita warna arsiran. Warna tunggal hanya
                                      menyatakan "di sini ada"; gradasi membuat
                                      satu luasan menyatakan seberapa jauh dan
                                      seberapa besar sekaligus. */}
                                  <Field label="GRADASI">
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3, alignItems: "center" }}>
                                    <button
                                      type="button"
                                      aria-label={`Shading of ${curve.name} in one colour`}
                                      title="Single colour"
                                      onClick={() =>
                                        onChange(
                                          updateCurve(tracks, track.id, curveIndex, {
                                            fill: { ...curve.fill!, ramp: undefined, reverse: undefined },
                                          }),
                                        )
                                      }
                                      style={{
                                        width: 26,
                                        height: 11,
                                        padding: 0,
                                        cursor: "pointer",
                                        borderRadius: 3,
                                        border: curve.fill.ramp
                                          ? "1px solid var(--border-control)"
                                          : "2px solid var(--ink)",
                                        background: curve.fill.color ?? colour,
                                      }}
                                    />
                                    {rampKeys().map((key) => (
                                      <button
                                        key={key}
                                        type="button"
                                        aria-label={`Shading of ${curve.name} graded by ${rampLabel(key)}`}
                                        title={rampLabel(key)}
                                        onClick={() =>
                                          onChange(
                                            updateCurve(tracks, track.id, curveIndex, {
                                              // Ramp yang baru dipilih mewarnai
                                              // menurut bacaan tiap kedalaman —
                                              // itu arti "bergradasi menurut
                                              // nilai" bagi yang memintanya, dan
                                              // itu pula yang dipakai log di
                                              // volume 3D. Lembar yang sudah
                                              // tersimpan tidak diubah: yang
                                              // tanpa `by` tetap melintang.
                                              fill: { ...curve.fill!, ramp: key,
                                                      by: curve.fill!.by ?? "value" },
                                            }),
                                          )
                                        }
                                        style={{
                                          width: 26,
                                          height: 11,
                                          padding: 0,
                                          cursor: "pointer",
                                          borderRadius: 3,
                                          border:
                                            key === curve.fill!.ramp
                                              ? "2px solid var(--ink)"
                                              : "1px solid var(--border-control)",
                                          background: rampGradient(key),
                                        }}
                                      />
                                    ))}
                                  </div>
                                  {curve.fill.ramp && (
                                    <EditColoursButton
                                      ramp={curve.fill.ramp}
                                      onRamp={(key) =>
                                        onChange(
                                          updateCurve(tracks, track.id, curveIndex, {
                                            fill: { ...curve.fill!, ramp: key },
                                          }),
                                        )
                                      }
                                    />
                                  )}
                                  </Field>
                                  {curve.fill.ramp && (
                                    <Field label="COLOUR FOLLOWS">
                                      <Segmented
                                        size="sm"
                                        value={curve.fill.by ?? "across"}
                                        onChange={(value) =>
                                          onChange(
                                            updateCurve(tracks, track.id, curveIndex, {
                                              fill: { ...curve.fill!, by: value as "across" | "value" },
                                            }),
                                          )
                                        }
                                        options={FILL_GRADIENT_OPTIONS}
                                      />
                                      <span
                                        style={{
                                          font: "400 9px/1.35 var(--font-sans)",
                                          color: "var(--ink-6)",
                                        }}
                                      >
                                        {(curve.fill.by ?? "across") === "value"
                                          ? `The whole width of the shading takes the colour of the ${curve.name} reading at that depth — the same colour scale as the 3D model.`
                                          : "The colour sweeps the ramp from the baseline to the reading; what states the reading is its outer edge."}
                                      </span>
                                    </Field>
                                  )}
                                  {curve.fill.ramp && (
                                    <Toggle
                                      label="Balik gradasi"
                                      checked={curve.fill.reverse === true}
                                      onChange={(on) =>
                                        onChange(
                                          updateCurve(tracks, track.id, curveIndex, {
                                            fill: { ...curve.fill!, reverse: on || undefined },
                                          }),
                                        )
                                      }
                                    />
                                  )}
                                </>
                              )}
                              {curve.fill && !curve.fill.ramp && (
                                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                  <span
                                    style={{
                                      font: "400 9px/1.35 var(--font-sans)",
                                      color: "var(--ink-6)",
                                      flex: 1,
                                    }}
                                  >
                                    {curve.fill.color
                                      ? "The shading carries its own colour."
                                      : `The shading follows the colour of ${curve.name}.`}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    title={`Return the shading to the colour of curve ${curve.name}`}
                                    onClick={() =>
                                      onChange(
                                        updateCurve(tracks, track.id, curveIndex, {
                                          fill: { from: curve.fill!.from },
                                        }),
                                      )
                                    }
                                  >
                                    Follow the curve
                                  </Button>
                                </div>
                              )}
                            </Field>
                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <span
                                style={{ font: "400 9px/1.35 var(--font-sans)", color: "var(--ink-6)", flex: 1 }}
                              >
                                {curve.min !== undefined || curve.max !== undefined
                                  ? "Scale set by hand. Left greater than right flips the axis."
                                  : "Using the curve's own display scale."}
                              </span>
                              <Button
                                variant="ghost"
                                title="Back to the curve's own display scale"
                                onClick={() =>
                                  onChange(
                                    updateCurve(tracks, track.id, curveIndex, {
                                      min: undefined,
                                      max: undefined,
                                    }),
                                  )
                                }
                              >
                                Auto
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                  {track.curves.length === 0 && (
                    <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)" }}>
                      {track.note
                        ? track.note
                        : track.lithology
                        ? "Lithology column — drawn from VSH, with no curve of its own."
                        : track.described
                          ? "Described lithology — core and cuttings, with no curve of its own."
                          : track.classes
                            ? "LRLC column — drawn from the published LRLC_CLASS curve, "
                              + "with no curve of its own."
                            : "This track holds no curve yet."}
                    </span>
                  )}
                </div>

                {!locked && spare.length > 0 && (
                  <Dropdown
                    value=""
                    options={[
                      { value: "", label: "Add curve…" },
                      ...spare.map((curve) => ({
                        value: curve.mnemonic,
                        label: curve.mnemonic,
                        hint: curve.unit || undefined,
                      })),
                    ]}
                    onChange={(value) => {
                      if (!value) return;
                      const style: CurveStyle = track.curves.length % 2 === 1 ? "dashed" : "solid";
                      onChange(addCurve(tracks, track.id, { name: String(value), style }));
                    }}
                    width="100%"
                    compact
                  />
                )}
              </>
            )}
          </Panel>
        );
      })}

      {!locked && (
      <Panel title="Sheet layout" bodyStyle={{ padding: 10, gap: 7 }}>
        <Button
          full
          onClick={() => {
            const track = makeTrack();
            onChange(addTrack(tracks, track));
            onSelect(track.id);
          }}
        >
          + New track
        </Button>
        {layout?.extras && (
        <Button
          full
          variant="ghost"
          disabled={tracks.some((track) => track.described)}
          title={
            tracks.some((track) => track.described)
              ? "This sheet already carries a described-lithology column"
              : "A column of the intervals described from core and cuttings"
          }
          onClick={() => {
            const track = makeDescribedTrack();
            onChange(addTrack(tracks, track));
            onSelect(track.id);
          }}
        >
          + Described lithology
        </Button>
        )}
        {layout?.extras && (
        <Button
          full
          variant="ghost"
          disabled={tracks.some((track) => track.classes)}
          title={
            tracks.some((track) => track.classes)
              ? "This sheet already carries an LRLC column"
              : "A column of the LRLC classes published from the LRLC screen"
          }
          onClick={() => {
            const track = makeLrlcTrack();
            onChange(addTrack(tracks, track));
            onSelect(track.id);
          }}
        >
          + LRLC class
        </Button>
        )}
        {available.length > 0 && (
          <Dropdown
            value=""
            options={[
              { value: "", label: "New track from curve…" },
              ...available.map((curve) => ({
                value: curve.mnemonic,
                label: curve.mnemonic,
                hint: `${curve.unit || "—"} · ${suggestedScale(curve.mnemonic) === "log" ? "log" : "lin"}`,
              })),
            ]}
            onChange={(value) => {
              if (!value) return;
              const source = available.find((curve) => curve.mnemonic === value);
              const track = makeTrack(String(value), source?.unit);
              onChange(addTrack(tracks, track));
              onSelect(track.id);
            }}
            width="100%"
          />
        )}
        {/* Kolom yang barusan dibuang, satu per satu.
            Di atas "Restore the default layout" dan bukan di bawahnya, sebab
            keduanya menjawab pertanyaan yang sama dengan harga yang sangat
            berbeda: yang satu mengembalikan kolom yang dicari, yang lain
            mengembalikan seluruh susunan bawaan dan membuang penyusunan yang
            baru dikerjakan orangnya. Yang lebih murah lebih dulu. */}
        {layout?.onRestoreOne && layout.restorable && layout.restorable.length > 0 && (
          <Dropdown
            value=""
            options={[
              { value: "", label: "Bring a column back…" },
              ...layout.restorable.map((track) => ({
                value: track.id,
                label: track.title,
              })),
            ]}
            onChange={(value) => {
              if (!value) return;
              layout.onRestoreOne!(String(value));
              onSelect(String(value));
            }}
            width="100%"
          />
        )}
        <Button
          variant="ghost"
          full
          onClick={() => {
            layout?.onRestore();
            onSelect(null);
          }}
        >
          Restore the default layout
        </Button>
      </Panel>
      )}
    </>
  );
}

/**
 * Arsiran silang yang sudah tentu utuh, dari setelan yang baru diubah sepotong.
 *
 * Ketiga medannya saling bergantung — dua nama kurva dan sebuah warna — dan
 * ketiga dropdownnya hanya mengubah satu di antaranya sekali jalan. Yang
 * dilengkapi di sini adalah dua medan yang tidak disebut, dari setelan yang
 * sedang berlaku atau dari kurva track ini sendiri, sehingga memilih sebuah
 * kurva pertama pada track yang belum punya arsiran langsung menghasilkan
 * arsiran yang tergambar alih-alih setengah setelan yang menunggu dua klik
 * lagi sebelum ada yang terjadi.
 */
function crossFill(track: TrackConfig, patch: Partial<CrossFill>,
                   fallback: string = CROSS_FILL_COLOR): CrossFill | undefined {
  const names = track.curves.map((entry) => entry.name);
  const curve = patch.curve ?? track.shading?.curve ?? names[0];
  const against =
    patch.against ?? (track.shading?.against !== curve ? track.shading?.against : undefined);
  const other = against ?? names.find((name) => name !== curve);
  if (!curve || !other || curve === other) return undefined;
  return { curve, against: other, color: patch.color ?? track.shading?.color ?? fallback };
}

/** Padanan CSS untuk bentuk garis, dipakai pada contoh garis di daftar kurva. */
function lineStyleName(style: CurveStyle | undefined): string {
  return style === "dotted" ? "dotted" : style && style !== "solid" ? "dashed" : "solid";
}

/**
 * Isi sebuah track yang panelnya sedang tertutup.
 *
 * Satu baris nama kurva berwarna sudah cukup menjawab pertanyaan yang membuat
 * seseorang menggulir daftar ini — "track mana yang memuat NPHI?" — tanpa
 * harus membuka kelimanya satu per satu.
 */
function CurveSummary({ track, available }: { track: TrackConfig; available: CurveData[] }) {
  if (!track.curves.length) {
    return (
      <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)" }}>
        {track.note
          ? track.note
          : track.lithology
            ? "Lithology column"
            : track.described
              ? "Described lithology"
              : track.classes
                ? "LRLC class column"
                : "Empty"}
      </span>
    );
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 8px" }}>
      {track.curves.map((curve) => {
        const absent = !available.some((entry) => entry.mnemonic === curve.name);
        return (
          <span
            key={curve.name}
            title={absent ? `${curve.name} is not on this well` : curve.name}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, opacity: absent || curve.hidden ? 0.45 : 1 }}
          >
            <span
              style={{
                width: 11,
                height: 0,
                flex: "none",
                borderTop: `2px ${lineStyleName(curve.style)} ${curve.color ?? defaultCurveColor(curve.name)}`,
              }}
            />
            <span
              style={{
                font: "400 9.5px var(--font-mono)",
                color: "var(--ink-4)",
                textDecoration: curve.hidden ? "line-through" : undefined,
              }}
            >
              {curve.name}
            </span>
          </span>
        );
      })}
    </div>
  );
}
